import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import DOMPurify from 'dompurify'; // Import DOMPurify
import PermissionGate from '../../../components/common/PermissionGate';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  Calendar,
  MapPin,
  Briefcase,
  Users,
  Clock,
  Download,
  X,
  Copy,
  CheckCircle,
  AlertCircle,
  Building,
  FileText,
  Upload,
  ArrowUp,
  ArrowDown,
  ArrowUpDown
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import jobService from '../../../api/services/jobService';
import departmentRoleService from '../../../api/services/departmentRoleService';
import locationService from '../../../api/services/locationService';
import { formatDate, capitalizeWords } from '../../../utils/stringUtils';
import toast from 'react-hot-toast';
import ExportModal from '../../../components/common/ExportModal';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import JobBulkUploadModal from './JobBulkUploadModal';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';

const DEFAULT_PAGE_SIZE = 10;

/** Must stay aligned with table / list API projection (no hidden fields in export). */
const JOB_LIST_EXPORT_FIELDS =
  '_id,jobId,title,department,location,type,experience,preferredGender,salary,status,priority,applicationsCount,postedDate,deadline,isActive';

const mapJobFromApi = (job = {}) => ({
  id: job._id,
  jobId: job.jobId,
  title: job.title,
  department: job.department,
  location: job.location,
  type:
    job.type === 'full_time'
      ? 'Full-time'
      : job.type === 'part_time'
        ? 'Part-time'
        : job.type,
  experience: job.experience?.name || 'N/A',
  preferredGender: job.preferredGender || 'any',
  salary: job.salary
    ? `₹${job.salary.min ?? 0} - ₹${job.salary.max ?? 0}`
    : '₹0 - ₹0',
  status: job.status === 'active'
    ? 'Active'
    : job.status === 'draft'
      ? 'Draft'
      : job.status === 'closed'
        ? 'Closed'
        : job.status,
  priority: job.priority,
  applications: job.applicationsCount ?? 0,
  postedDate: job.postedDate,
  deadline: job.deadline,
  description: job.description,
  requirements: job.requirements,
  skills: job.skills,
  isActive: job.isActive,
});

const normalizeJobsResponse = (payload, fallbackPagination = {}) => {
  const pageSize = fallbackPagination.pageSize || DEFAULT_PAGE_SIZE;
  const currentPage = fallbackPagination.currentPage || 1;

  const rawJobs = Array.isArray(payload?.jobs)
    ? payload.jobs
    : Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload)
        ? payload
        : [];

  const paginationSource = payload?.pagination || {};
  const totalCount =
    typeof paginationSource.totalCount === 'number'
      ? paginationSource.totalCount
      : rawJobs.length;

  const resolvedPageSize =
    paginationSource.pageSize || pageSize || DEFAULT_PAGE_SIZE;
  const resolvedCurrentPage = paginationSource.currentPage || currentPage || 1;
  const totalPages =
    paginationSource.totalPages ||
    Math.max(1, Math.ceil(totalCount / resolvedPageSize));

  const pagination = {
    currentPage: resolvedCurrentPage,
    pageSize: resolvedPageSize,
    totalCount,
    totalPages,
    hasNextPage:
      typeof paginationSource.hasNextPage === 'boolean'
        ? paginationSource.hasNextPage
        : resolvedCurrentPage < totalPages,
    hasPrevPage:
      typeof paginationSource.hasPrevPage === 'boolean'
        ? paginationSource.hasPrevPage
        : resolvedCurrentPage > 1,
  };

  return {
    jobs: rawJobs.map(mapJobFromApi),
    pagination,
  };
};

const JobPosts = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const PAGE_SIZE = DEFAULT_PAGE_SIZE;

  // Initialize state from URL search params
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || "");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchParams.get('search') || "");
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [bulkActionToConfirm, setBulkActionToConfirm] = useState('');
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || 'active');
  const [filterDepartment, setFilterDepartment] = useState(searchParams.get('department') || 'all');
  const [filterLocation, setFilterLocation] = useState(searchParams.get('location') || 'all');
  const [minSalary, setMinSalary] = useState(searchParams.get('minSalary') || '');
  const [maxSalary, setMaxSalary] = useState(searchParams.get('maxSalary') || '');
  const [actionLoading, setActionLoading] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);
  const [itemsPerPage, setItemsPerPage] = useState(Number(searchParams.get('limit')) || 10);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] =
    useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] =
    useState(false);
  const selectedExportWrapRef = useRef(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: searchParams.get('startDate') || '',
    endDate: searchParams.get('endDate') || ''
  });
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [sortColumn, setSortColumn] = useState(searchParams.get('sortBy') || 'postedDate');
  const [sortDirection, setSortDirection] = useState(searchParams.get('sortOrder') || 'desc');

  const jobTypes = [
    "Full-time",
    "Part-time",
    "Contract",
    "Internship",
    "Remote",
  ];
  const priorities = ["High", "Medium", "Low"];

  const experienceLevels = [
    "0-1 years",
    "1-2 years",
    "2-3 years",
    "3-5 years",
    "5-7 years",
    "7-10 years",
    "10+ years",
  ];

  // Load jobs from API
  // Debounce search term (500ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Sync filter state to URL search params
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);

      // Managed keys for this component
      const managedKeys = ['search', 'status', 'department', 'location', 'minSalary', 'maxSalary', 'startDate', 'endDate', 'page', 'limit', 'sortBy', 'sortOrder'];
      managedKeys.forEach(key => params.delete(key));

      if (debouncedSearchTerm) params.set('search', debouncedSearchTerm);
      if (filterStatus && filterStatus !== 'active') params.set('status', filterStatus);
      if (filterDepartment && filterDepartment !== 'all') params.set('department', filterDepartment);
      if (filterLocation && filterLocation !== 'all') params.set('location', filterLocation);
      if (minSalary) params.set('minSalary', minSalary);
      if (maxSalary) params.set('maxSalary', maxSalary);
      if (dateRange.startDate) params.set('startDate', dateRange.startDate);
      if (dateRange.endDate) params.set('endDate', dateRange.endDate);
      if (currentPage && currentPage !== 1) params.set('page', String(currentPage));
      if (itemsPerPage && itemsPerPage !== 10) params.set('limit', String(itemsPerPage));
      if (sortColumn && sortColumn !== 'postedDate') params.set('sortBy', sortColumn);
      if (sortDirection && sortDirection !== 'desc') params.set('sortOrder', sortDirection);

      return params;
    }, { replace: true });
  }, [debouncedSearchTerm, filterStatus, filterDepartment, filterLocation, minSalary, maxSalary, dateRange, currentPage, itemsPerPage, sortColumn, sortDirection, setSearchParams]);

  // Keyboard shortcut for clearing filters (Escape key)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && hasActiveFilters()) {
        clearAllFilters();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [searchTerm, filterStatus, filterDepartment, filterLocation, minSalary, maxSalary, dateRange]);

  useEffect(() => {
    if (selectedJobs.length === 0) {
      setShowInlineSelectedFormats(false);
    }
  }, [selectedJobs.length]);

  useEffect(() => {
    if (!showInlineSelectedFormats) {
      setInlineFormatPanelEntered(false);
      return;
    }
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setInlineFormatPanelEntered(true))
    );
    return () => cancelAnimationFrame(id);
  }, [showInlineSelectedFormats]);

  useEffect(() => {
    if (!showInlineSelectedFormats) return;
    const onMouseDown = (e) => {
      if (
        selectedExportWrapRef.current &&
        !selectedExportWrapRef.current.contains(e.target)
      ) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);

  const baseFilters = useMemo(() => {
    const params = {};
    const trimmedSearch = debouncedSearchTerm.trim();

    if (trimmedSearch) {
      params.search = trimmedSearch;
    }

    if (filterStatus !== 'all') {
      params.status = filterStatus === 'active' ? 'active' : filterStatus;
    }

    if (filterDepartment !== 'all') {
      params.department = filterDepartment;
    }

    if (filterLocation !== 'all') {
      params.location = filterLocation;
    }

    if (
      minSalary &&
      !Number.isNaN(parseFloat(minSalary)) &&
      parseFloat(minSalary) >= 0
    ) {
      params.minSalary = parseFloat(minSalary).toString();
    }

    if (
      maxSalary &&
      !Number.isNaN(parseFloat(maxSalary)) &&
      parseFloat(maxSalary) >= 0
    ) {
      params.maxSalary = parseFloat(maxSalary).toString();
    }

    if (dateRange.startDate) {
      params.startDate = dateRange.startDate;
    }

    if (dateRange.endDate) {
      params.endDate = dateRange.endDate;
    }

    return params;
  }, [debouncedSearchTerm, filterStatus, filterDepartment, filterLocation, minSalary, maxSalary, dateRange]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const queryParams = useMemo(
    () => ({
      ...baseFilters,
      paginated: 'true',
      page: currentPage,
      limit: itemsPerPage,
      sortBy: sortColumn,
      sortOrder: sortDirection,
      fields: JOB_LIST_EXPORT_FIELDS
    }),
    [baseFilters, currentPage, itemsPerPage, sortColumn, sortDirection]
  );
  const {
    data: jobsQueryData,
    isLoading: loading,
    error: jobsError,
    isFetching,
    refetch: refetchJobs
  } = useQuery({
    queryKey: ['jobs', queryParams],
    queryFn: async () => {
      const result = await jobService.getJobs(queryParams);

      const payload = result?.data ?? result ?? [];
      const normalized = normalizeJobsResponse(payload, {
        currentPage,
        pageSize: itemsPerPage
      });

      return normalized;
    },
    keepPreviousData: true,
    staleTime: 0,
    retry: 2,
    onError: (error) => {
      if (error.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        setTimeout(() => {
          window.location.href = "/login";
        }, 2000);
      }
    },
  });

  const jobPosts = jobsQueryData?.jobs || [];
  const pagination = jobsQueryData?.pagination || {
    currentPage,
    pageSize: itemsPerPage,
    totalCount: jobPosts.length,
    totalPages: Math.max(1, Math.ceil(Math.max(jobPosts.length, 1) / itemsPerPage)),
    hasNextPage: false,
    hasPrevPage: currentPage > 1,
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [
    debouncedSearchTerm,
    filterStatus,
    filterDepartment,
    filterLocation,
    minSalary,
    maxSalary,
    dateRange
  ]);

  // React Query: Fetch status counts
  const filtersWithoutStatus = useMemo(() => {
    const { status, ...rest } = baseFilters;
    return rest;
  }, [baseFilters]);

  const { data: statusCountsData } = useQuery({
    queryKey: ['jobStatusCounts', filtersWithoutStatus],
    queryFn: async () => {
      const fetchCount = async (statusValue) => {
        const params = {
          ...filtersWithoutStatus,
          paginated: 'true',
          page: 1,
          limit: 1,
          fields: '_id'
        };

        if (statusValue) {
          params.status = statusValue;
        }

        const response = await jobService.getJobs(params);
        const payload = response?.data ?? response ?? [];
        const normalized = normalizeJobsResponse(payload, {
          currentPage: 1,
          pageSize: 1,
        });
        return normalized.pagination.totalCount || 0;
      };

      const [active, draft, closed, all] = await Promise.all([
        fetchCount('active'),
        fetchCount('draft'),
        fetchCount('closed'),
        fetchCount(null),
      ]);

      return { active, draft, closed, all };
    },
    keepPreviousData: true,
    staleTime: 0,
    retry: 2,
    onError: () => { },
  });

  const statusCounts = statusCountsData || { active: 0, draft: 0, closed: 0, all: 0 };

  // React Query: Fetch departments
  const {
    data: departmentsData,
    isLoading: loadingDepartments
  } = useQuery({
    queryKey: ['departments'],
    queryFn: async () => {
      const result = await departmentRoleService.getDepartments();

      let departmentsData = [];

      if (Array.isArray(result)) {
        departmentsData = result;
      } else if (result && result.data) {
        departmentsData = Array.isArray(result.data) ? result.data : result.data.departments || [];
      } else if (result && result.responseCode === 200) {
        departmentsData = result.data?.departments || result.data || [];
      } else {
        departmentsData = [];
      }

      const departmentNames = departmentsData.map(dept => {
        if (typeof dept === 'string') {
          return dept;
        } else if (dept && typeof dept === 'object') {
          const name = dept.name || dept.departmentName || dept.title || dept.department || dept._id || '';
          return name;
        }
        return '';
      }).filter(Boolean);
      return departmentNames;
    },
    staleTime: 0,
    retry: 2,
    onError: () => { },
  });

  const departments = departmentsData || [];

  // React Query: Fetch locations
  const {
    data: locationsData,
    isLoading: loadingLocations
  } = useQuery({
    queryKey: ['activeLocations', 'names'],
    queryFn: async () => {
      const result = await locationService.getActiveLocations();

      if (result.success && result.data) {
        // Handle different response structures
        // apiClient returns response.data which is the full API response object
        // So result.data might be: { responseCode, responseMessage, data: [...] }
        // Or it might be the array directly
        let locationsArray = [];

        // Check if result.data is the full API response object with nested data
        if (result.data && result.data.data && Array.isArray(result.data.data)) {
          locationsArray = result.data.data;
        }
        // Check if result.data is directly an array
        else if (Array.isArray(result.data)) {
          locationsArray = result.data;
        }
        // Check if result.data has a locations property
        else if (result.data.locations && Array.isArray(result.data.locations)) {
          locationsArray = result.data.locations;
        }

        // Extract location names from objects, ensuring we always return strings
        const locationNames = locationsArray.map(loc => {
          if (typeof loc === 'string') {
            return loc;
          } else if (loc && typeof loc === 'object') {
            // Prioritize 'value' property for the new location structure
            if (loc.value) {
              return loc.value;
            }
            // Handle different location object structures
            const name = loc.name || loc.label || loc.value;
            if (name && typeof name === 'string') {
              return name;
            }
            // Fallback: try to stringify the object
            return String(loc);
          }
          return String(loc);
        }).filter(loc => loc && typeof loc === 'string'); // Ensure we only return strings

        return locationNames;
      } else {
        // Fallback to hardcoded locations
        return ['Mumbai Office', 'Delhi Office', 'Bangalore Office', 'Chennai Office', 'Remote'];
      }
    },
    staleTime: 0,
    retry: 2,
    onError: () => { },
  });

  // Ensure locations is always an array of strings
  const locations = Array.isArray(locationsData)
    ? locationsData.filter(loc => typeof loc === 'string')
    : ['Mumbai Office', 'Delhi Office', 'Bangalore Office', 'Chennai Office', 'Remote'];

  const handleEdit = (job) => {
    navigate(`/edit-job/${job.id}`);
  };

  const handleDelete = async (jobId) => {
    setJobToDelete(jobId);
    setShowDeleteConfirm(true);
  };

  // React Query: Delete job mutation
  const deleteJobMutation = useMutation({
    mutationFn: async (jobId) => {
      const result = await jobService.deleteJob(jobId);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['jobStatusCounts'] });
      toast.success('Job post deleted successfully!');
      setShowDeleteConfirm(false);
      setJobToDelete(null);
    },
    onError: () => {
      toast.error("Error deleting job. Please try again.");
      setShowDeleteConfirm(false);
      setJobToDelete(null);
    },
  });

  const confirmDelete = async () => {
    if (!jobToDelete) return;
    deleteJobMutation.mutate(jobToDelete);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setJobToDelete(null);
  };

  const handleViewDetails = (job) => {
    navigate(`/view-job/${job.id}`);
  };

  const handleCopyJob = async (job) => {
    try {
      const jobIdentifier = job.jobId || job.id;

      if (!jobIdentifier) {
        toast.error('Unable to generate job link. Job ID not found.');
        return;
      }

      const jobLink = `${window.location.origin}/job-application/${jobIdentifier}`;

      await navigator.clipboard.writeText(jobLink);
      toast.success("Job link copied to clipboard!");
    } catch (error) {
      const jobIdentifier = job.jobId || job.id;

      if (!jobIdentifier) {
        toast.error('Unable to generate job link. Job ID not found.');
        return;
      }

      const jobLink = `${window.location.origin}/job-application/${jobIdentifier}`;

      const textArea = document.createElement('textarea');
      textArea.value = jobLink;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        toast.success("Job link copied to clipboard!");
      } catch (fallbackError) {
        toast.error("Failed to copy job link. Please try again.");
      }
      document.body.removeChild(textArea);
    }
  };

  const handleBulkStatusUpdate = async () => {
    if (bulkUpdateMutation.isPending) {
      return;
    }

    if (selectedJobs.length === 0) {
      toast.error('Please select jobs to update');
      return;
    }

    if (!bulkAction) {
      toast.error('Please select an action');
      return;
    }

    setBulkActionToConfirm(bulkAction);
    setShowBulkConfirm(true);
  };

  // React Query: Bulk update mutation
  const bulkUpdateMutation = useMutation({
    mutationFn: async ({ jobIds, status }) => {
      const promises = jobIds.map(jobId => {
        return jobService.updateJob(jobId, { status: status.toLowerCase() });
      });
      const results = await Promise.all(promises);
      return results;
    },
    onSuccess: (results) => {
      const failedUpdates = results.filter(result => !result || !result.success);
      if (failedUpdates.length > 0) {
        toast.error(`${failedUpdates.length} job(s) failed to update.`);
      } else {
        toast.success(`${selectedJobs.length} job(s) marked as ${bulkActionToConfirm} successfully!`);
      }

      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['jobStatusCounts'] });

      setSelectedJobs([]);
      setBulkAction('');
      setShowBulkConfirm(false);
      setBulkActionToConfirm('');
    },
    onError: () => {
      toast.error('Error updating jobs. Please try again.');
      setShowBulkConfirm(false);
      setBulkActionToConfirm('');
    },
  });

  // React Query: Bulk delete mutation
  const bulkDeleteMutation = useMutation({
    mutationFn: async (jobIds) => {
      const result = await jobService.bulkDeleteJobs(jobIds);
      return result;
    },
    onSuccess: (result) => {
      if (result.success) {
        toast.success(`${selectedJobs.length} job(s) deleted successfully!`);
      } else {
        toast.error(result.error || 'Failed to delete some jobs.');
      }

      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['jobStatusCounts'] });

      setSelectedJobs([]);
      setBulkAction('');
      setShowBulkConfirm(false);
      setBulkActionToConfirm('');
    },
    onError: (error) => {
      toast.error(error.message || 'Error deleting jobs. Please try again.');
      setShowBulkConfirm(false);
      setBulkActionToConfirm('');
    },
  });

  const confirmBulkAction = async () => {
    if (!bulkActionToConfirm) return;

    if (bulkActionToConfirm === 'delete') {
      bulkDeleteMutation.mutate(selectedJobs);
    } else {
      bulkUpdateMutation.mutate({
        jobIds: selectedJobs,
        status: bulkActionToConfirm
      });
    }
  };

  const cancelBulkUpdate = () => {
    setShowBulkConfirm(false);
    setBulkActionToConfirm('');
  };

  const handleSelectJob = (jobId) => {
    setSelectedJobs((prev) =>
      prev.includes(jobId)
        ? prev.filter((id) => id !== jobId)
        : [...prev, jobId]
    );
  };

  const handleBulkActionChange = (action) => {
    setBulkAction(action);
  };

  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
    }) => {
      const response = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`, {
        credentials: 'include',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
                      },
          body: JSON.stringify({
            module: 'job-posts',
            format,
            scope,
            filters: overrideFilters ?? baseFilters,
            page: overridePage ?? currentPage,
            limit: overrideLimit ?? itemsPerPage,
            sortBy: sortColumn,
            sortOrder: sortDirection,
            selectedIds,
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Export request failed');
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `job-posts-export.${format === 'xlsx' ? 'xlsx' : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [baseFilters, currentPage, itemsPerPage, sortColumn, sortDirection]
  );

  const handleExportClick = () => {
    if (selectedJobs.length > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  const handleJobPostsSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      if (!selectedJobs.length) {
        toast.error('No selected jobs found to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: selectedJobs,
        });
        toast.success(`${selectedJobs.length} job(s) exported!`);
        setShowInlineSelectedFormats(false);
      } catch (error) {
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedJobs, requestBackendExport]
  );

  const handleJobPostsScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) {
        return;
      }

      const posts = jobsQueryData?.jobs || [];
      const pag =
        jobsQueryData?.pagination || {
          currentPage,
          pageSize: itemsPerPage,
          totalCount: posts.length,
          totalPages: Math.max(
            1,
            Math.ceil(Math.max(posts.length, 1) / itemsPerPage)
          ),
          hasNextPage: false,
          hasPrevPage: currentPage > 1,
        };

      setExportLoading(true);
      try {
        if (scope === 'currentPage' && !posts.length) {
          toast.error('No jobs to export on current page.');
          return;
        }

        if (scope === 'filtered') {
          const total = pag?.totalCount ?? 0;
          if (total === 0) {
            toast.error('No jobs match the current filters.');
            return;
          }
        }

        if (scope === 'dateRange') {
          const startDate = meta?.startDate;
          const endDate = meta?.endDate;
          if (!startDate || !endDate) {
            toast.error('Please select start and end dates.');
            return;
          }
          await requestBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: { ...baseFilters, startDate, endDate },
          });
        } else if (scope === 'all') {
          await requestBackendExport({
            scope: 'all',
            format,
            overrideFilters: {},
            overridePage: 1,
            overrideLimit: 1000,
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            scope: 'currentPage',
            format,
          });
        } else {
          return;
        }

        toast.success('Export downloaded.');
        setShowExportModal(false);
      } catch (error) {
        toast.error(error?.message || 'Export failed. Please try again.');
      } finally {
        setExportLoading(false);
      }
    },
    [jobsQueryData, currentPage, itemsPerPage, baseFilters, requestBackendExport]
  );

  // Check if any filters are active
  const hasActiveFilters = () => {
    return (
      searchTerm.trim() !== '' ||
      filterStatus !== 'active' ||
      filterDepartment !== 'all' ||
      filterLocation !== 'all' ||
      minSalary !== '' ||
      maxSalary !== '' ||
      dateRange.startDate !== '' ||
      dateRange.endDate !== ''
    );
  };

  // Count active filters
  const getActiveFilterCount = () => {
    let count = 0;
    if (searchTerm.trim() !== '') count++;
    if (filterStatus !== 'active') count++;
    if (filterDepartment !== 'all') count++;
    if (filterLocation !== 'all') count++;
    if (minSalary !== '') count++;
    if (maxSalary !== '') count++;
    if (dateRange.startDate !== '') count++;
    if (dateRange.endDate !== '') count++;
    return count;
  };

  const formatDepartmentLabel = (dept) => {
    if (!dept) return '';
    const normalized = String(dept).trim();
    return normalized.length > 20 ? `${normalized.slice(0, 20)}…` : normalized;
  };

  // Clear all filters function
  const clearAllFilters = () => {
    setSearchTerm('');
    setFilterStatus('active');
    setFilterDepartment('all');
    setFilterLocation('all');
    setMinSalary('');
    setMaxSalary('');
    setDateRange({ startDate: '', endDate: '' });
    setSelectedJobs([]);
    setCurrentPage(1);
    setItemsPerPage(10);
  };

  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
    setCurrentPage(1);
  };

  const paginatedJobs = jobPosts;
  const totalJobs = pagination.totalCount || 0;
  const totalPages = pagination.totalPages || 1;
  const safePage = pagination.currentPage || 1;
  const showingFrom =
    totalJobs === 0 ? 0 : (safePage - 1) * pagination.pageSize + 1;
  const showingTo =
    totalJobs === 0 ? 0 : showingFrom + paginatedJobs.length - 1;

  const allJobsSelectedOnPage =
    paginatedJobs.length > 0 &&
    paginatedJobs.every((job) => selectedJobs.includes(job.id));

  const handleSelectAll = () => {
    if (allJobsSelectedOnPage) {
      setSelectedJobs((prev) =>
        prev.filter((id) => !paginatedJobs.some((job) => job.id === id))
      );
    } else {
      setSelectedJobs((prev) => {
        const pageJobIds = paginatedJobs.map((job) => job.id);
        const newIds = pageJobIds.filter((id) => !prev.includes(id));
        return [...prev, ...newIds];
      });
    }
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const getStatusColor = (status) => {
    if (!status) return "bg-gray-100 text-gray-800";
    switch (status.toLowerCase()) {
      case "active":
        return "bg-green-100 text-green-800";
      case "draft":
        return "bg-yellow-100 text-yellow-800";
      case "closed":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getPriorityColor = (priority) => {
    if (!priority) return "bg-gray-100 text-gray-800";
    switch (priority.toLowerCase()) {
      case "high":
        return "bg-red-100 text-red-800";
      case "medium":
        return "bg-yellow-100 text-yellow-800";
      case "low":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };



  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between !mt-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Job Postings</h1>
        </div>
        <div className="flex items-center space-x-4">
          {selectedJobs.length > 0 && (
            <PermissionGate permission="recruitment" action="delete">
              <Button
                variant="danger"
                icon={<Trash2 className="h-4 w-4" />}
                onClick={() => {
                  setBulkActionToConfirm('delete');
                  setShowBulkConfirm(true);
                }}
                loading={bulkDeleteMutation.isPending}
              >
                Delete Selected ({selectedJobs.length})
              </Button>
            </PermissionGate>
          )}
          <PermissionGate permission="recruitment" action="read">
            <div className="relative shrink-0" ref={selectedExportWrapRef}>
              <Button
                variant="outline"
                icon={<Download className="h-4 w-4" />}
                onClick={handleExportClick}
                disabled={exportLoading}
                className={
                  selectedJobs.length > 0
                    ? 'bg-blue-50 border-blue-200 text-blue-600'
                    : ''
                }
              >
                {selectedJobs.length > 0
                  ? `Export Selected (${selectedJobs.length})`
                  : 'Export'}
              </Button>
              <SelectedExportFormatBar
                show={
                  selectedJobs.length > 0 && showInlineSelectedFormats
                }
                panelEntered={inlineFormatPanelEntered}
                disabled={exportLoading}
                onSelect={handleJobPostsSelectedFormatExport}
              />
            </div>
          </PermissionGate>
          <PermissionGate permission="recruitment" action="create">
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                icon={<Upload className="h-4 w-4" />}
                onClick={() => setShowBulkUploadModal(true)}
                className="border-agrosaf-green text-agrosaf-green hover:bg-agrosaf-green/5"
              >
                Bulk Upload
              </Button>
              <Button
                variant="primary"
                icon={<Plus className="h-4 w-4" />}
                onClick={() => navigate("/create-job")}
                className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
              >
                Create Job Posting
              </Button>
            </div>
          </PermissionGate>
        </div>
      </div>

      {/* Status Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex flex-wrap space-x-2 sm:space-x-8">
          <button
            onClick={() => setFilterStatus('active')}
            className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${filterStatus === 'active'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            <span className="hidden sm:inline">Active Jobs</span>
            <span className="sm:hidden">Active</span> ({statusCounts.active})
          </button>
          <button
            onClick={() => setFilterStatus('draft')}
            className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${filterStatus === 'draft'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            <span className="hidden sm:inline">Draft Jobs</span>
            <span className="sm:hidden">Draft</span> ({statusCounts.draft})
          </button>
          <button
            onClick={() => setFilterStatus('closed')}
            className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${filterStatus === 'closed'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            <span className="hidden sm:inline">Closed Jobs</span>
            <span className="sm:hidden">Closed</span> ({statusCounts.closed})
          </button>
          <button
            onClick={() => setFilterStatus('all')}
            className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${filterStatus === 'all'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            <span className="hidden sm:inline">All Jobs</span>
            <span className="sm:hidden">All</span> ({statusCounts.all})
          </button>
        </nav>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-end gap-4 w-full">
          {/* Search */}
          <div className="relative flex-[2] min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by job title, department, location, job ID, or skills..."
              value={searchTerm}
              maxLength={100}
              onChange={(e) => {
                // Only allow alphanumeric characters and spaces 
                let filteredValue = e.target.value.replace(/[^a-zA-Z0-9\s]/g, '');
                // Enforce max length
                if (filteredValue.length > 100) {
                  filteredValue = filteredValue.substring(0, 100);
                }
                setSearchTerm(filteredValue);
              }}
              onKeyPress={(e) => {
                if (!/^[a-zA-Z0-9\s]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
            />
          </div>

          {/* Department Filter */}
          <div className="flex-[1.2] min-w-[200px]">
            <select
              value={filterDepartment}
              onChange={(e) => setFilterDepartment(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
            >
              <option value="all">All Departments</option>
              {departments.map(dept => (
                <option key={dept} value={dept} title={dept}>
                  {formatDepartmentLabel(dept)}
                </option>
              ))}
            </select>
          </div>

          {/* Location Filter */}
          <div className="flex-shrink-0">
            <select
              value={filterLocation}
              onChange={(e) => setFilterLocation(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
            >
              <option value="all">All Locations</option>
              {locations.map((location, index) => {
                // Ensure location is always a string
                const locationString = typeof location === 'string'
                  ? location
                  : (location?.name || location?.label || location?.value || String(location) || `Location ${index}`);
                return (
                  <option key={locationString} value={locationString}>{capitalizeWords(locationString)}</option>
                );
              })}
            </select>
          </div>

          {/* Salary Range */}
          <div className="flex-shrink-0">
            <div className="flex items-center space-x-2">
              <input
                type="number"
                placeholder="Min Salary (₹)"
                value={minSalary}
                onChange={(e) => {
                  let value = e.target.value;
                  // Remove any non-numeric characters
                  value = value.replace(/[^0-9]/g, '');
                  // Ensure value is 0 or greater
                  if (value && Number(value) < 0) {
                    value = '0';
                  }
                  // Enforce max value (100 million)
                  if (value && Number(value) > 100000000) {
                    value = '100000000';
                  }
                  setMinSalary(value);
                }}
                onKeyDown={(e) => {
                  // Allow: backspace, delete, tab, escape, enter, arrow keys, and numbers
                  if ([8, 9, 27, 13, 46, 37, 38, 39, 40].indexOf(e.keyCode) !== -1 ||
                    // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
                    (e.keyCode === 65 && e.ctrlKey === true) ||
                    (e.keyCode === 67 && e.ctrlKey === true) ||
                    (e.keyCode === 86 && e.ctrlKey === true) ||
                    (e.keyCode === 88 && e.ctrlKey === true) ||
                    // Allow numbers on top of keyboard
                    (e.keyCode >= 48 && e.keyCode <= 57) ||
                    // Allow numbers on numpad
                    (e.keyCode >= 96 && e.keyCode <= 105)) {
                    return;
                  }
                  // Block: minus, plus, e, E, period (for decimal)
                  if (e.keyCode === 189 || e.keyCode === 187 || e.keyCode === 69 || e.keyCode === 190 || e.keyCode === 110) {
                    e.preventDefault();
                  }
                }}
                min="0"
                max="100000000"
                step="1000"
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent w-32"
              />
              <span className="text-gray-500 font-medium">to</span>
              <input
                type="number"
                placeholder="Max Salary (₹)"
                value={maxSalary}
                onChange={(e) => {
                  let value = e.target.value;
                  // Remove any non-numeric characters
                  value = value.replace(/[^0-9]/g, '');
                  // Ensure value is 0 or greater
                  if (value && Number(value) < 0) {
                    value = '0';
                  }
                  // Enforce max value (100 million)
                  if (value && Number(value) > 100000000) {
                    value = '100000000';
                  }
                  setMaxSalary(value);
                }}
                onKeyDown={(e) => {
                  // Allow: backspace, delete, tab, escape, enter, arrow keys, and numbers
                  if ([8, 9, 27, 13, 46, 37, 38, 39, 40].indexOf(e.keyCode) !== -1 ||
                    // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
                    (e.keyCode === 65 && e.ctrlKey === true) ||
                    (e.keyCode === 67 && e.ctrlKey === true) ||
                    (e.keyCode === 86 && e.ctrlKey === true) ||
                    (e.keyCode === 88 && e.ctrlKey === true) ||
                    // Allow numbers on top of keyboard
                    (e.keyCode >= 48 && e.keyCode <= 57) ||
                    // Allow numbers on numpad
                    (e.keyCode >= 96 && e.keyCode <= 105)) {
                    return;
                  }
                  // Block: minus, plus, e, E, period (for decimal)
                  if (e.keyCode === 189 || e.keyCode === 187 || e.keyCode === 69 || e.keyCode === 190 || e.keyCode === 110) {
                    e.preventDefault();
                  }
                }}
                min="0"
                max="100000000"
                step="1000"
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent w-32"
              />
            </div>
          </div>

          {/* Date Range Filter */}
          <div className="flex-shrink-0">
            <DateRangeFilter
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={setDateRange}
            />
          </div>

          {/* Clear Filters */}
          {hasActiveFilters() && (
            <div className="relative flex-shrink-0">
              <Button
                variant="outline"
                icon={<X className="h-4 w-4" />}
                onClick={clearAllFilters}
              >
                Clear
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Bulk Actions */}


      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-agrosaf-green"></div>
          <p className="mt-2 text-gray-600">Loading jobs...</p>
        </div>
      )}

      {/* Job Table View */}
      {!loading && paginatedJobs.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {selectedJobs.length > 0 && (
            <div className="bg-blue-50 px-6 py-3 border-b border-blue-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center space-x-3">
                <span className="text-sm font-medium text-blue-700">
                  {selectedJobs.length} jobs selected
                </span>
                <button
                  onClick={() => setSelectedJobs([])}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  Clear Selection
                </button>
              </div>

              <div className="flex items-center space-x-2">
                <select
                  value={bulkAction}
                  onChange={(e) => handleBulkActionChange(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-blue-900"
                >
                  <option value="">Status Action</option>
                  <option value="active">Mark as Active</option>
                  <option value="draft">Mark as Draft</option>
                  <option value="closed">Mark as Closed</option>
                  <option value="delete">Delete Selected</option>
                </select>
                {bulkAction && (
                  <PermissionGate permission="recruitment" action={bulkAction === 'delete' ? 'delete' : 'update'}>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleBulkStatusUpdate}
                      loading={bulkUpdateMutation.isPending || bulkDeleteMutation.isPending}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      Apply
                    </Button>
                  </PermissionGate>
                )}
              </div>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left w-10">
                    <input
                      type="checkbox"
                      checked={allJobsSelectedOnPage}
                      onChange={handleSelectAll}
                      className="h-4 w-4 text-agrosaf-green border-gray-300 rounded focus:ring-agrosaf-green"
                    />
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('title')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Job Details</span>
                      {sortColumn === 'title' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('department')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Department</span>
                      {sortColumn === 'department' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('type')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Category</span>
                      {sortColumn === 'type' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('preferredGender')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Gender</span>
                      {sortColumn === 'preferredGender' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('applicationsCount')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Applicants</span>
                      {sortColumn === 'applicationsCount' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-px whitespace-nowrap cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center justify-center space-x-1">
                      <span>Status / Priority</span>
                      {sortColumn === 'status' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('postedDate')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Posted Date & Time</span>
                      {sortColumn === 'postedDate' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedJobs.map((job) => (
                  <tr key={job.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedJobs.includes(job.id)}
                        onChange={() => handleSelectJob(job.id)}
                        className="h-4 w-4 text-agrosaf-green border-gray-300 rounded focus:ring-agrosaf-green"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col max-w-xs">
                        <span className="text-sm font-semibold text-gray-900 capitalize truncate" title={job.title}>
                          {job.title}
                        </span>
                        <div className="flex items-center space-x-2 mt-0.5">
                          <span className="text-xs text-gray-500">ID: {job.jobId}</span>
                          <span className="text-xs text-gray-400">•</span>
                          <span className="text-xs font-medium text-gray-600">{job.salary}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col text-sm text-gray-600">
                        <div className="flex items-center">
                          <Building className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
                          <span className="capitalize truncate max-w-[150px]" title={job.department}>{job.department}</span>
                        </div>
                        <div className="flex items-center mt-1 text-xs text-gray-500">
                          <MapPin className="h-3 w-3 mr-1 text-gray-400" />
                          <span className="capitalize">{job.location}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <div className="flex items-center text-xs text-gray-600">
                          <Briefcase className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
                          <span className="capitalize">{job.type}</span>
                        </div>
                        <div className="flex items-center text-xs text-gray-500 mt-1">
                          <Clock className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
                          <span>{job.experience}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900 capitalize">
                        {job.preferredGender}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-700">
                        <Users className="h-4 w-4 mr-1.5 text-gray-400" />
                        <span className="font-medium">{job.applications}</span>
                      </div>
                    </td>
                    <td className="px-2 py-4 whitespace-nowrap w-px">
                      <div className="flex flex-col items-center space-y-1">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${getStatusColor(job.status)}`}>
                          {job.status}
                        </span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider ${getPriorityColor(job.priority)}`}>
                          {job.priority} Priority
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {job.postedDate ? formatDate(job.postedDate, { includeTime: true }) : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <PermissionGate permission="recruitment" action="read">
                          <button
                            onClick={() => handleCopyJob(job)}
                            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                            title="Copy Job Link"
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                        <PermissionGate permission="recruitment" action="read">
                          <button
                            onClick={() => handleViewDetails(job)}
                            className="p-1 text-blue-500 hover:text-blue-700 transition-colors"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                        <PermissionGate permission="recruitment" action="update">
                          <button
                            onClick={() => handleEdit(job)}
                            className="p-1 text-indigo-500 hover:text-indigo-700 transition-colors"
                            title="Edit Job"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                        <PermissionGate permission="recruitment" action="delete">
                          <button
                            onClick={() => handleDelete(job.id)}
                            className="p-1 text-red-500 hover:text-red-700 transition-colors"
                            title="Delete Job"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div >
      )}


      {/* Empty State */}
      {
        !loading && totalJobs === 0 && (
          <div className="text-center py-12">
            <Briefcase className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No job posts found
            </h3>
            <p className="text-gray-500 mb-4">
              {searchTerm ||
                filterStatus !== "all" ||
                filterDepartment !== "all" ||
                filterLocation !== "all"
                ? "Try adjusting your search or filter criteria."
                : "Get started by creating your first job post."}
            </p>
            {!searchTerm &&
              filterStatus === "all" &&
              filterDepartment === "all" &&
              filterLocation === "all" && (
                <PermissionGate permission="recruitment" action="create">
                  <Button
                    variant="primary"
                    icon={<Plus className="h-4 w-4" />}
                    onClick={() => navigate("/create-job")}
                    className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                  >
                    Create Job Post
                  </Button>
                </PermissionGate>
              )}
          </div>
        )
      }

      {!loading && pagination.totalPages > 1 && (
        <PaginationControls
          currentPage={pagination.currentPage || 1}
          totalPages={pagination.totalPages || 1}
          totalItems={pagination.totalCount || 0}
          limit={itemsPerPage}
          hasNextPage={pagination.hasNextPage}
          hasPrevPage={pagination.hasPrevPage}
          onPageChange={handlePageChange}
          onLimitChange={handleItemsPerPageChange}
        />
      )}



      {/* Delete Confirmation Modal */}
      {
        showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                  Delete Job Post
                </h3>
                <p className="text-gray-600 text-center mb-6">
                  Are you sure you want to delete this job post? This action
                  cannot be undone.
                </p>
                <div className="flex space-x-3">
                  <Button
                    variant="secondary"
                    onClick={cancelDelete}
                    className="flex-1"
                    disabled={deleteJobMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <PermissionGate permission="recruitment" action="delete">
                    <Button
                      variant="danger"
                      onClick={confirmDelete}
                      className="flex-1"
                      disabled={deleteJobMutation.isPending}
                    >
                      {deleteJobMutation.isPending ? 'Deleting...' : 'Delete'}
                    </Button>
                  </PermissionGate>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Bulk Action Confirmation Modal */}
      {
        showBulkConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className={`flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full ${bulkActionToConfirm === 'delete' ? 'bg-red-100' : 'bg-blue-100'}`}>
                  {bulkActionToConfirm === 'delete' ? (
                    <Trash2 className="h-6 w-6 text-red-600" />
                  ) : (
                    <CheckCircle className="h-6 w-6 text-blue-600" />
                  )}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                  {bulkActionToConfirm === 'delete' ? 'Confirm Bulk Delete' : 'Confirm Bulk Update'}
                </h3>
                <p className="text-gray-600 text-center mb-6">
                  {bulkActionToConfirm === 'delete' ? (
                    <>Are you sure you want to delete <strong>{selectedJobs.length}</strong> job(s)? This action cannot be undone.</>
                  ) : (
                    <>Are you sure you want to mark <strong>{selectedJobs.length}</strong> job(s) as <strong>{bulkActionToConfirm}</strong>?</>
                  )}
                </p>
                <div className="flex space-x-3">
                  <Button
                    variant="secondary"
                    onClick={cancelBulkUpdate}
                    className="flex-1"
                    disabled={bulkUpdateMutation.isPending || bulkDeleteMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <PermissionGate permission="recruitment" action={bulkActionToConfirm === 'delete' ? 'delete' : 'update'}>
                    <Button
                      variant={bulkActionToConfirm === 'delete' ? 'danger' : 'primary'}
                      onClick={confirmBulkAction}
                      className="flex-1"
                      disabled={bulkUpdateMutation.isPending || bulkDeleteMutation.isPending}
                    >
                      {bulkUpdateMutation.isPending || bulkDeleteMutation.isPending ? 'Processing...' : bulkActionToConfirm === 'delete' ? 'Delete' : 'Confirm'}
                    </Button>
                  </PermissionGate>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Job Posts"
        showDateRange
        scopedFormatExport={{
          onExport: handleJobPostsScopedExport,
          formats: ['csv', 'xlsx', 'pdf'],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />
      {/* Bulk Upload Modal */}
      <JobBulkUploadModal
        isOpen={showBulkUploadModal}
        onClose={() => setShowBulkUploadModal(false)}
        onSuccess={() => {
          refetchJobs();
          queryClient.invalidateQueries({ queryKey: ['jobStatusCounts'] });
        }}
      />

    </div >
  );
};

export default JobPosts;